import React, { PureComponent } from 'react'
import PropTypes from 'prop-types'
import Emitter from 'tiny-emitter'
import { connect } from 'react-redux'
import throttle from 'lodash/throttle'
import { toast } from 'react-toastify'

import { BASE_URL } from '../../js/constants'
import { fetchTerminalVisits, fetchBerthsOccupation, changeTerminalVisits } from '../app/actions'
import { loginWithRefreshToken } from '../../js/rest'
import { setNewToken, clearAuth } from '../../pages/login/actions'
import { handleFetchError } from '../../js/sentry'

// duration of the throttling of terminal visit changes in
const THROTTLE_TERMINAL_VISIT_CHANGES = 500 // milliseconds

const PING_INTERVAL = 60 * 1000 // milliseconds
const PING_TIMEOUT = 2 * 1000 // milliseconds

class VisitsWebSocket extends PureComponent {
  static propTypes = {
    children: PropTypes.any.isRequired,
    customFetch: PropTypes.func.isRequired,
    currentTerminalId: PropTypes.string.isRequired
  }

  constructor(props) {
    super(props)

    // an array with queued terminal visit changes, which are applied in batches
    // Structure of a terminal visit change:
    //
    //   {action: 'CREATE' | 'UPDATE' | 'DELETE', terminalVisitId: string, terminalVisit: Objct}
    // Here:
    // - action is 'CREATE', 'UPDATE', or 'DELETE'
    // - terminalVisit only available in case of 'CREATE' or 'UPDATE'
    this.queuedTerminalVisitChanges = []

    // @ts-ignore
    this.websocketMessages = new Emitter()
    this.websocketMessages.on('TerminalVisitChangeV2', terminalVisitChange => {
      this.queuedTerminalVisitChanges.push(terminalVisitChange)
      this.applyTerminalVisitChanges() // throttled function
    })
  }

  componentDidMount() {
    this.openWebSocket()
    this.startPingInterval()
  }

  componentDidUpdate(prevProps) {
    if (this.props.currentTerminalId !== prevProps.currentTerminalId) {
      this.reconnectWebSocket()
    }
  }

  componentWillUnmount() {
    clearInterval(this.pingInterval)
    this.closeWebSocket()
  }

  render() {
    return <React.Fragment>{this.props.children}</React.Fragment>
  }

  /**
   * Load berth occupancy from the backend
   */
  loadBerthOccupancy = () => {
    const { from, to } = this.getDataRange()
    this.props.dispatch(
      fetchBerthsOccupation(
        this.props.login.auth.terminalId,
        from,
        to,
        this.props.login.auth.token,
        this.props.customFetch
      )
    )
  }

  /**
   * Throttled function to apply all queued terminal visit changes
   */
  applyTerminalVisitChanges = throttle(() => {
    const terminalVisitChanges = this.queuedTerminalVisitChanges
    this.queuedTerminalVisitChanges = []
    this.props.dispatch(changeTerminalVisits(terminalVisitChanges))

    if (this.props.app.present.config.quays[0].berths) {
      this.loadBerthOccupancy()
    }
  }, THROTTLE_TERMINAL_VISIT_CHANGES)

  /**
   * Open websocket connection
   */
  openWebSocket() {
    console.info('opening websocket...')

    const baseUrl = BASE_URL.replace(/^http/, 'ws')
    const url = `${baseUrl}/v1/websocket/terminalVisits?token=${this.props.login.auth.token}&terminalId=${this.props.currentTerminalId}`

    // TODO: automatically reconnecting websocket. Tried https://github.com/joewalnes/reconnecting-websocket but has some issues
    this.ws = new WebSocket(url)

    this.ws.onopen = () => {
      console.info('opened websocket')

      // Dismiss websocket error toast
      if (toast.isActive('websocket')) {
        toast.dismiss()
        toast.success('Reconnect succesful')
      }
    }
    this.ws.onclose = () => console.error('Websocket closed...')
    this.ws.onerror = error => console.error(error)
    this.ws.onmessage = event => {
      const envelope = JSON.parse(event.data) // envelope is an object {type: string, contents: any}
      console.info('received message over websocket:', envelope)

      if (envelope && envelope.type) {
        this.websocketMessages.emit(envelope && envelope.type, envelope.contents || null)
      }
    }
  }

  /**
   * Close the websocket connection to the backend
   */
  closeWebSocket() {
    if (this.ws) {
      this.ws.close()
      this.ws = null
    }
  }

  async reconnectWebSocket() {
    this.closeWebSocket()

    try {
      // Get a new auth token
      const newAuth = await loginWithRefreshToken(this.props.login.auth.username, this.props.login.auth.refreshToken)
      await this.props.dispatch(setNewToken(newAuth.token, newAuth.refreshToken))
    } catch (err) {
      if (err.status === 401) {
        console.error('Unable to login again using the refresh token', err)
        this.props.dispatch(clearAuth())
      } else {
        handleFetchError(err)
      }
    }

    this.openWebSocket()
  }

  startPingInterval() {
    // stop previous interval if any
    clearInterval(this.pingInterval)

    const handleWebSocketError = async () => {
      const webSocketError = new Error('WebSocket not responding. Reconnecting...')
      console.error(webSocketError)
      toast.error('WebSocket not responding. Reconnecting...', { autoClose: false, toastId: 'websocket' })

      await this.reconnectWebSocket()

      // fetch all data again, we may have missed changes whilst the websocket was down
      const { from, to } = this.getDataRange()
      this.props.dispatch(
        fetchTerminalVisits(
          this.props.login.auth.terminalId,
          from,
          to,
          this.props.app.present.config,
          this.props.login.auth.token,
          this.props.customFetch
        )
      )
    }

    this.pingInterval = setInterval(() => {
      if (this.ws && this.ws.readyState === WebSocket.OPEN) {
        // set a timeout timer, and clear this timeout as soon as we receive a pong message
        const pingTimeout = setTimeout(handleWebSocketError, PING_TIMEOUT)
        this.websocketMessages.once('pong', () => clearTimeout(pingTimeout))
        this.ws.send(JSON.stringify({ type: 'ping' }))
      } else {
        if (!this.ws || this.ws.readyState === WebSocket.CLOSED || this.ws.readyState === WebSocket.CLOSING) {
          handleWebSocketError()
        }
      }
    }, PING_INTERVAL)
  }

  getDataRange = () => {
    return {
      from: this.props.app.present.dataRange.from,
      to: this.props.app.present.dataRange.to
    }
  }
}

export default connect(state => state)(VisitsWebSocket)
